"use client";

import { useRef } from "react";
import { ArrowRight, Brain, CloudCog, GitBranch, Link2, ShieldCheck, ShieldHalf } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Aisearchlandscape() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".tf-header", {
        y: 20
      });
      gsap.to(".tf-header", {
        opacity: 1,
        y: 0,
        duration: 0.64,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".tf-card", {
        y: 24
      });
      gsap.to(".tf-card", {
        opacity: 1,
        y: 0,
        duration: 0.64,
        ease: "power3.out",
        stagger: 0.1,
        scrollTrigger: {
          trigger: ".tf-grid",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".tf-header", ".tf-card"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section data-section-id="31372" id="aisearchlandscape" data-nav="light" className="bg-background text-foreground py-24 md:py-44 px-6">
      <div ref={container} className="mx-auto w-full max-w-[1360px]">
        <div className="tf-header mx-auto max-w-[760px] text-center" style={{
        opacity: 0
      }}>
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            Search Landscape Breakdown
          </p>
          <h2 className="mt-6 font-default text-2xl sm:text-3xl lg:text-4xl font-medium leading-[1.06] tracking-tight text-foreground">
            What Is <span className="text-primary">AI Search Optimisation</span>?
          </h2>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            These are overlapping views of one job, not separate budgets.
          </p>
        </div>

        <div className="tf-grid mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-auto md:auto-rows-[minmax(180px,auto)]">
          {/* Card 0: Definition */}
          <Card data-index={0} className="tf-card md:col-span-7 md:row-span-2 border border-border bg-card backdrop-blur-xl rounded-lg p-8 md:p-10 transition-all duration-300 hover:-translate-y-1 hover:border-primary shadow-md" style={{
          opacity: 0
        }}>
            <div className="flex flex-col h-full">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-muted">
                <ShieldCheck className="h-6 w-6 text-primary" strokeWidth={1.25} />
              </div>
              <h3 className="mt-8 font-default text-2xl md:text-3xl font-medium tracking-tight text-foreground">
                AI Search Optimisation defined.
              </h3>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground max-w-lg">
                AI Search Optimisation is the practice of improving visibility, accuracy and relevance across AI-powered search and answer engines, such as ChatGPT, Google AI Overviews, Perplexity and others. It works by making your site and brand easy for AI systems to find, understand and reference when answering real buyer questions. It builds directly on traditional{" "}
                <Link to="/TechnicalSeoPage" className="font-medium text-foreground underline underline-offset-4 hover:text-primary transition-colors">
                  technical SEO
                </Link>{" "}
                foundations—including crawlability, content quality, authority and technical health.
              </p>

              <div className="mt-10 flex-1 flex items-end">
                <svg viewBox="0 0 480 160" className="w-full h-auto max-h-[180px]" aria-hidden="true">
                  <defs>
                    <linearGradient id="tfLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="hsl(220 80% 30%)" stopOpacity="0.2" />
                      <stop offset="50%" stopColor="hsl(220 80% 30%)" stopOpacity="0.9" />
                      <stop offset="100%" stopColor="hsl(220 80% 30%)" stopOpacity="0.2" />
                    </linearGradient>
                  </defs>
                  <line x1="80" y1="80" x2="240" y2="80" stroke="url(#tfLine)" strokeWidth="1.5" />
                  <line x1="240" y1="80" x2="400" y2="80" stroke="url(#tfLine)" strokeWidth="1.5" />
                  {[80, 240, 400].map((cx, i) => <g key={i} data-index={i}>
                      <circle cx={cx} cy="80" r="28" fill="hsl(0 0% 100%)" stroke="hsl(220 80% 30%)" strokeWidth="1.25" />
                      <circle cx={cx} cy="80" r="42" fill="none" stroke="hsl(220 80% 30%)" strokeOpacity="0.15" strokeWidth="1" />
                      <path d={`M ${cx - 8} 76 v -4 a 8 8 0 0 1 16 0 v 4 M ${cx - 10} 76 h 20 v 12 h -20 z`} fill="none" stroke="hsl(220 80% 30%)" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                    </g>)}
                </svg>
              </div>
            </div>
          </Card>

          {/* Card 1: LLM optimisation (replaces old Traditional SEO vs AI Search) */}
          <Card data-index={1} className="tf-card md:col-span-5 border border-border bg-card backdrop-blur-xl rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary shadow-md" style={{
          opacity: 0
        }}>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted">
              <CloudCog className="h-5 w-5 text-primary" strokeWidth={1.25} />
            </div>
            <h3 className="mt-6 font-default text-xl font-medium tracking-tight text-foreground">
              LLM optimisation.
            </h3>
            <p className="mt-3 text-[17px] leading-relaxed text-muted-foreground">
              LLM optimisation examines how content is structured so large language models can read, verify and reference it during retrieval. Clear factual statements, explicit definitions and clean document hierarchy make extraction reliable.
            </p>
          </Card>

          {/* Card 2: AI SEO */}
          <Card data-index={2} className="tf-card md:col-span-5 border border-border bg-card backdrop-blur-xl rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary shadow-md" style={{
          opacity: 0
        }}>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted">
              <Brain className="h-5 w-5 text-primary" strokeWidth={1.25} />
            </div>
            <h3 className="mt-6 font-default text-xl font-medium tracking-tight text-foreground">
              AI SEO.
            </h3>
            <p className="mt-3 text-[17px] leading-relaxed text-muted-foreground">
              The term is used loosely in the industry and often means using AI tools to produce content. I use it to mean SEO work for AI search, not AI-written content.
            </p>
          </Card>

          {/* Card 3: Answer Engine Optimisation (AEO) */}
          <Card data-index={3} className="tf-card md:col-span-4 border border-border bg-card backdrop-blur-xl rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary shadow-md" style={{
          opacity: 0
        }}>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted">
              <GitBranch className="h-5 w-5 text-primary" strokeWidth={1.25} />
            </div>
            <h3 className="mt-6 font-default text-xl font-medium tracking-tight text-foreground">
              Answer engine optimisation (AEO).
            </h3>
            <p className="mt-3 text-[17px] leading-relaxed text-muted-foreground">
              Clear question-and-answer content for surfaces that give direct answers: featured snippets, voice search, and Perplexity-style answer engines that pull specific passages.
            </p>
          </Card>

          {/* Card 4: Generative Engine Optimisation (GEO) */}
          <Card data-index={4} className="tf-card md:col-span-4 border border-border bg-card backdrop-blur-xl rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary shadow-md" style={{
          opacity: 0
        }}>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted">
              <Link2 className="h-5 w-5 text-primary" strokeWidth={1.25} />
            </div>
            <h3 className="mt-6 font-default text-xl font-medium tracking-tight text-foreground">
              Generative engine optimisation (GEO).
            </h3>
            <p className="mt-3 text-[17px] leading-relaxed text-muted-foreground">
              The broadest umbrella: making your brand a credible source when generative tools summarise the web. As an{" "}
              <Link to="/FreelanceSeoConsultantPage" className="font-medium text-foreground underline underline-offset-4 hover:text-primary transition-colors">
                independent SEO consultant
              </Link>
              , I help ensure your core propositions are clear and verifiable.
            </p>
          </Card>

          {/* Card 5: What I avoid */}
          <Card data-index={5} className="tf-card md:col-span-4 rounded-lg p-8 transition-all duration-300 hover:-translate-y-1 shadow-md border border-secondary bg-secondary" style={{
          opacity: 0
        }}>
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-background/20">
              <ShieldHalf className="h-5 w-5 text-secondary-foreground" strokeWidth={1.25} />
            </div>
            <h3 className="mt-6 font-default text-xl font-medium tracking-tight text-secondary-foreground">
              What I avoid.
            </h3>
            <p className="mt-3 text-[17px] leading-relaxed text-secondary-foreground/80">
              Not hacks such as llms.txt tricks or attempts to manipulate models; these are unreliable and often short-lived. Nobody can promise inclusion in an AI answer or in model training data.
            </p>
            <Button asChild variant="link" className="mt-4 h-auto p-0 font-medium text-secondary-foreground group">
              <Link to="/ContactPage">
                Book a consultation
                <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-240 group-hover:translate-x-1" />
              </Link>
            </Button>
          </Card>
        </div>
      </div>
    </section>;
}